import { LocalStorageContainer } from '@frontend/shared/src/helpers/LocalStorageContainer';
import { ReactiveWallpaper } from '@frontend/auction/src/reactive/ReactiveWallpaper';
import { FileStorage } from './FileStorageService';

const DEFAULT_WALLPAPER = new ReactiveWallpaper(0, {
  src: '/auction/assets/bg.webp',
  css: 'repeat;',
  color: '#02776d',
  type: 'image',
});

const MAX_WALLPAPERS = 18;

const Storage = new LocalStorageContainer<{
  selected_id: number;
  wallpapers: {
    id: number;
    src: string | null;
    file_id: string | null;
    css: string | null;
    color: string | null;
    type: 'image' | 'video' | null;
  }[];
}>('__WALLPAPERS__');

class WallpapersService {
  private _image = () => document.querySelector<HTMLImageElement>('body > img.wallpaper');
  private _video = () => document.querySelector<HTMLVideoElement>('body > video.wallpaper');

  private _selected_id = DEFAULT_WALLPAPER.id;

  readonly wallpapers = Array.from({ length: MAX_WALLPAPERS }).map((_, i) =>
    i === 0 ? DEFAULT_WALLPAPER : new ReactiveWallpaper(i)
  );

  get selected_id() {
    return this._selected_id;
  }

  private _save = () => {
    Storage.set({
      selected_id: this._selected_id,
      wallpapers: this.wallpapers.map(wallpaper => {
        if (wallpaper.src?.startsWith('blob')) {
          return { ...wallpaper.state, src: null };
        } else {
          return wallpaper.state;
        }
      }),
    });
  };

  private _reset = () => {
    this.select(this.wallpapers[0]!.id);
  };

  private _setWallpaper = async (wallpaper: ReactiveWallpaper) => {
    const image = this._image();
    const video = this._video();

    if (image) {
      if (wallpaper.type === 'image') {
        image.src = wallpaper.src ?? '';

        if (wallpaper.css) {
          image.style.cssText = wallpaper.css;

          if (wallpaper.css.includes('repeat')) {
            image.style.cssText += `; background-image: url(${wallpaper.src}); background-repeat: repeat; `;
            image.src = '';
          }
        } else {
          image.style.cssText = '';
        }
      } else {
        image.src = '';
        image.style.cssText = '';
      }
    }

    if (video) {
      if (wallpaper.type === 'video') {
        video.src = wallpaper.src ?? '';

        if (wallpaper.css) {
          video.style.cssText = wallpaper.css;
        } else {
          video.style.cssText = '';
        }
      } else {
        video.src = '';
        video.style.cssText = '';
      }
    }

    document.body.style.backgroundColor = wallpaper.color ?? '';
  };

  init = () => {
    let stored = Storage.get();

    if (stored) {
      this.wallpapers.forEach(wallpaper => {
        const storedWallpaper = stored.wallpapers.find(
          storedWallpaper => storedWallpaper.id === wallpaper.id
        );

        if (storedWallpaper) {
          wallpaper.src =
            storedWallpaper.src === '/assets/bg.webp' ? '/auction/assets/bg.webp' : wallpaper.src;

          wallpaper.file_id = storedWallpaper.file_id;
          wallpaper.css = storedWallpaper.css;
          wallpaper.color = storedWallpaper.color;
          wallpaper.type = storedWallpaper.type;
        }
      });

      this.select(stored.selected_id);
    } else {
      this._reset();
    }

    this.wallpapers.forEach(wallpaper => {
      const handleCurrentWallpaperChange = () => {
        if (wallpaper.id === this.selected_id) {
          this._setWallpaper(wallpaper);
        }
      };

      wallpaper.onPropertyChange('src', handleCurrentWallpaperChange);
      wallpaper.onPropertyChange('color', handleCurrentWallpaperChange);
      wallpaper.onPropertyChange('css', handleCurrentWallpaperChange);
    });

    return this;
  };

  select = (id: number) => {
    const wallpaper = this.wallpapers.find(wallpaper => wallpaper.id === id);

    if (wallpaper) {
      this._selected_id = id;

      this._setWallpaper(wallpaper);

      this._save();
    }
  };

  remove = (id: number) => {
    const wallpaper = this.wallpapers.find(wallpaper => wallpaper.id === id);

    if (wallpaper) {
      if (wallpaper.src?.startsWith('blob')) {
        URL.revokeObjectURL(wallpaper.src);
      }

      if (wallpaper.file_id) {
        FileStorage.delete(wallpaper.file_id);
      }

      wallpaper.clear();

      if (id === this._selected_id) {
        this._reset();
      } else {
        this._save();
      }
    }
  };
}

export const Wallpapers = new WallpapersService().init();
